import { useState } from "react";
import { api, ApiClientError } from "../lib/api";
import { applySyncedProfile, getDeviceProfile } from "../lib/device-profile";
import Icon from "./Icon";

interface Props {
  /** Called after a code is successfully redeemed, so the parent can re-read the (now updated) device profile. */
  onSynced: () => void;
  /** "redeem-only" hides the "generate a code" side — used where there's no local profile yet to generate one from (the first-visit nickname screen). */
  mode?: "both" | "redeem-only";
  /** Rendered already expanded, with no collapse/close control — used inline on the nickname screen rather than as a toggle link. */
  startOpen?: boolean;
}

// A single collapsible panel covering both directions of device-to-device
// sync: generate a code here to hand to another device, or type in a code
// generated on another device. No accounts, no passwords — just a
// short-lived, one-time code (functions/lib/device-sync.ts).
export default function DeviceSyncPanel({ onSynced, mode = "both", startOpen = false }: Props) {
  const [open, setOpen] = useState(startOpen);
  const [code, setCode] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function generateCode() {
    const profile = getDeviceProfile();
    if (!profile) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await api.createSyncCode({ nickname: profile.nickname, sender: profile.sender as Record<string, string | undefined> | undefined });
      setCode(result.code);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't generate a code — please try again.");
    } finally {
      setGenerating(false);
    }
  }

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — the code is already shown on screen to copy by hand.
    }
  }

  async function redeem() {
    if (!codeInput.trim()) return;
    setRedeeming(true);
    setError(null);
    try {
      const profile = await api.redeemSyncCode(codeInput.trim());
      applySyncedProfile({ nickname: profile.nickname, sender: profile.sender });
      setSuccess(true);
      onSynced();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't sync — please try again.");
    } finally {
      setRedeeming(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-ghost"
        style={{ width: "auto", minHeight: "auto", padding: "var(--space-2)" }}
        onClick={() => setOpen(true)}
      >
        <Icon name="sliders" size={16} strokeWidth={2} /> Sync with another device
      </button>
    );
  }

  if (success) {
    return (
      <div className="banner banner-success">
        <span>✓ Synced — your name and details from your other device are now saved here too.</span>
      </div>
    );
  }

  return (
    <div className={mode === "redeem-only" ? "stack" : "card stack"} style={{ textAlign: "left" }}>
      {mode === "both" && (
        <div className="stack-sm">
          <strong>On this device: get a code</strong>
          <p className="field-help" style={{ margin: 0 }}>
            Generate a code, then enter it on your other device to copy your name and details over.
          </p>
          {code ? (
            <div className="stack-sm">
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: "1.8rem",
                  fontWeight: 800,
                  letterSpacing: "0.15em",
                  textAlign: "center",
                  padding: "var(--space-3)",
                  background: "var(--color-primary-tint)",
                  color: "var(--color-primary)",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                {code}
              </div>
              <button type="button" className="btn btn-secondary" onClick={copyCode}>
                {copied ? "Copied!" : "Copy code"}
              </button>
              <p className="field-help" style={{ textAlign: "center", margin: 0 }}>
                Expires in 15 minutes, and can only be used once.
              </p>
            </div>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={generateCode} disabled={generating}>
              {generating ? "Generating…" : "Generate code"}
            </button>
          )}
        </div>
      )}

      <div className={mode === "both" ? "stack-sm" : "stack-sm card"} style={mode === "both" ? { borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-4)" } : undefined}>
        <strong>Have a code from another device?</strong>
        <div className="field">
          <input
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && redeem()}
            placeholder="Enter code"
            maxLength={6}
            style={{ fontFamily: "monospace", fontSize: "1.2rem", letterSpacing: "0.1em", textAlign: "center" }}
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={redeem} disabled={redeeming || !codeInput.trim()}>
          {redeeming ? "Syncing…" : "Sync now"}
        </button>
        {error && <span className="field-error">{error}</span>}
      </div>

      {!startOpen && (
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>
          Close
        </button>
      )}
    </div>
  );
}
